import React, { useState, useEffect, useRef } from "react";
import firebase from "../../firebase";
import { getVendorId } from "../../utils/rbac";
import "./walkinform.css";

// ─── helpers ─────────────────────────────────────────────────────────────────
const db = () => firebase.firestore();

const today = () => new Date().toISOString().slice(0, 10);
const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// ─── component ────────────────────────────────────────────────────────────────
const WalkInForm = ({ onClose } = {}) => {
  const vendorId = getVendorId();

  // customer
  const [name, setName]           = useState("");
  const [phone, setPhone]         = useState("");
  const [phoneErr, setPhoneErr]   = useState("");
  const [suggestions, setSuggestions]   = useState([]);
  const [showSuggest, setShowSuggest]   = useState(false);
  const [customerSelected, setCustomerSelected] = useState(false);
  const suggestRef = useRef(null);

  // services
  const [services, setServices]         = useState([]);   // all from Firestore
  const [categories, setCategories]     = useState([]);
  const [selectedCat, setSelectedCat]   = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);   // [{...svc, price, stylistId, stylistName}]
  const [editedPrices, setEditedPrices] = useState({});  // serviceId -> string price

  // stylists
  const [stylists, setStylists]         = useState([]);
  const [svcStylists, setSvcStylists]   = useState({});  // serviceId -> {id,name}
  const [defaultStylist, setDefaultStylist] = useState(null);

  // schedule
  const [scheduleDate, setScheduleDate] = useState(today());
  const [scheduleTime, setScheduleTime] = useState(nowTime());
  const [paymentMode, setPaymentMode]   = useState("Cash");

  // ui
  const [saving, setSaving]     = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg]     = useState("");

  // ── fetch services ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!vendorId) return;
    const unsub = db()
      .collection("services")
      .where("vendorId", "==", vendorId)
      .orderBy("categoryName")
      .onSnapshot((snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setServices(docs);
        const cats = [...new Set(docs.map((d) => d.categoryName).filter(Boolean))];
        setCategories(cats);
        if (cats.length && !selectedCat) setSelectedCat(cats[0]);
      });
    return () => unsub();
  }, [vendorId]); // eslint-disable-line

  // ── fetch stylists ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!vendorId) return;
    const unsub = db()
      .collection("stylists")
      .where("vendorId", "==", vendorId)
      .where("active", "==", true)
      .onSnapshot((snap) => setStylists(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [vendorId]);

  // ── outside click hides suggestions ────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target))
        setShowSuggest(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── customer search ─────────────────────────────────────────────────────────
  const searchCustomers = async (q) => {
    if (q.length < 2) { setSuggestions([]); setShowSuggest(false); return; }
    const isPhone = /^\d+$/.test(q);
    let snap;
    if (isPhone) {
      snap = await db().collection("customers")
        .where("vendors", "array-contains", vendorId)
        .where("phone", ">=", q).where("phone", "<", q + "z")
        .limit(8).get();
    } else {
      const lower = q.toLowerCase();
      snap = await db().collection("customers")
        .where("vendors", "array-contains", vendorId)
        .where("nameLower", ">=", lower).where("nameLower", "<", lower + "z")
        .limit(8).get();
      if (snap.empty) {
        const fallback = await db().collection("customers")
          .where("vendors", "array-contains", vendorId).limit(50).get();
        const filtered = fallback.docs.filter((d) =>
          (d.data().name || "").toLowerCase().includes(lower));
        setSuggestions(filtered.map((d) => ({ id: d.id, ...d.data() })));
        setShowSuggest(true);
        return;
      }
    }
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setSuggestions(list);
    setShowSuggest(list.length > 0);
  };

  const selectCustomer = (c) => {
    setName(c.name || "");
    setPhone(c.phone || "");
    setCustomerSelected(true);
    setShowSuggest(false);
    setSuggestions([]);
  };

  // ── service helpers ─────────────────────────────────────────────────────────
  const categorySvcs = services.filter((s) => s.categoryName === selectedCat);
  const groupsInCat  = [...new Set(categorySvcs.map((s) => s.group || "").filter(Boolean))].sort();

  const filteredSvcs = serviceSearch.trim()
    ? services.filter((s) =>
        (s.serviceName || "").toLowerCase().includes(serviceSearch.toLowerCase()) ||
        (s.group || "").toLowerCase().includes(serviceSearch.toLowerCase()) ||
        (s.categoryName || "").toLowerCase().includes(serviceSearch.toLowerCase()))
    : categorySvcs.filter((s) =>
        !selectedGroup || (s.group || "") === selectedGroup);

  const isSvcSelected = (serviceId) => selectedServices.some((s) => s.serviceId === serviceId);

  const toggleService = (svc) => {
    const id = svc.serviceId || svc.id;
    if (isSvcSelected(id)) {
      setSelectedServices((prev) => prev.filter((s) => s.serviceId !== id));
      setSvcStylists((prev) => { const n = { ...prev }; delete n[id]; return n; });
      setEditedPrices((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } else {
      const entry = { ...svc, serviceId: id };
      setSelectedServices((prev) => [...prev, entry]);
      setEditedPrices((prev) => ({ ...prev, [id]: String(svc.offerPrice || svc.price || 0) }));
      setSvcStylists((prev) => ({ ...prev, [id]: defaultStylist || null }));
    }
  };

  // ── totals ──────────────────────────────────────────────────────────────────
  const totalPrice = selectedServices.reduce((sum, s) => {
    return sum + (parseInt(editedPrices[s.serviceId] || s.offerPrice || s.price || 0, 10));
  }, 0);
  const totalTime = selectedServices.reduce((sum, s) =>
    sum + parseInt(s.serviceTime || s.time || 0, 10), 0);

  // ── set default stylist (auto-assign to all) ────────────────────────────────
  const applyDefaultStylist = (stylist) => {
    setDefaultStylist(stylist);
    const updated = {};
    selectedServices.forEach((s) => { updated[s.serviceId] = stylist; });
    setSvcStylists((prev) => ({ ...prev, ...updated }));
  };

  // ── validation ──────────────────────────────────────────────────────────────
  const validatePhone = (p) => {
    const clean = p.replace(/\D/g, "");
    if (!clean) return "Enter phone number";
    if (clean.length !== 10) return "Must be exactly 10 digits";
    return "";
  };

  // ── save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const pErr = validatePhone(phone);
    if (pErr) { setPhoneErr(pErr); return; }
    setPhoneErr("");

    if (!name.trim()) { setErrorMsg("Enter customer name"); return; }
    if (selectedServices.length === 0) { setErrorMsg("Select at least one service"); return; }

    const unassigned = selectedServices.filter((s) => !svcStylists[s.serviceId]);
    if (unassigned.length > 0) {
      setErrorMsg(`Assign stylist to: ${unassigned.map((s) => s.serviceName).join(", ")}`);
      return;
    }

    setSaving(true);
    setErrorMsg("");

    try {
      // build services list with edited prices + per-service stylist
      const svcList = selectedServices.map((s) => ({
        name: s.serviceName,
        price: parseInt(editedPrices[s.serviceId] || s.offerPrice || s.price || 0, 10),
        time: s.serviceTime || s.time || 0,
        subCatId: s.serviceId,
        vendorId: s.vendorId,
        stylistId: svcStylists[s.serviceId]?.id,
        stylistName: svcStylists[s.serviceId]?.name,
      }));

      // build stylistBreakdown
      const breakdown = {};
      svcList.forEach((s) => {
        if (!breakdown[s.stylistId]) {
          breakdown[s.stylistId] = { name: s.stylistName, revenue: 0, serviceCount: 0 };
        }
        breakdown[s.stylistId].revenue += s.price;
        breakdown[s.stylistId].serviceCount += 1;
      });
      const stylistIds = Object.keys(breakdown);
      const primaryStylist = svcStylists[selectedServices[0].serviceId];

      // fetch vendor doc for salonName / salonAddress
      const vendorDoc = await db().collection("vendors").doc(vendorId).get();
      const vendor = vendorDoc.exists ? vendorDoc.data() : {};

      const bookingId = `ZC${Date.now()}`;
      const docRef = db().collection("appointments").doc();

      await docRef.set({
        bookingId,
        id: docRef.id,
        isWalkIn:      true,
        isCallBooking: false,

        userName: name.trim(),
        phone: phone.trim(),
        userId: phone.trim(),

        vendorId,
        salonName: vendor.salonName || "",
        salonAddress: vendor.salonAddress || "",

        stylistId: primaryStylist?.id || "",
        stylistName: primaryStylist?.name || "",
        stylistIds,
        stylistBreakdown: breakdown,

        services: svcList,
        TotalServicePrice: totalPrice,
        finalPrice: totalPrice,

        scheduleDate,
        scheduleTime,
        createdAt: scheduleDate,
        record_timestamp: firebase.firestore.FieldValue.serverTimestamp(),

        status: "Completed",
        paymentMode,
        paymentStatus: "Paid",

        homeOrder: false,
        discountAmount: 0,
        couponCode: null,
      });

      // upsert customer
      const custRef = db().collection("customers").doc(phone.trim());
      const custSnap = await custRef.get();
      const vendorStatsUpdate = {
        vendorStats: {
          [vendorId]: {
            visits: firebase.firestore.FieldValue.increment(1),
            spent: firebase.firestore.FieldValue.increment(totalPrice),
            lastVisit: firebase.firestore.FieldValue.serverTimestamp(),
          },
        },
      };

      if (custSnap.exists) {
        await custRef.set({
          name: name.trim(),
          phone: phone.trim(),
          lastVisit: firebase.firestore.FieldValue.serverTimestamp(),
          totalSpent: firebase.firestore.FieldValue.increment(totalPrice),
          visitCount: firebase.firestore.FieldValue.increment(1),
          vendors: firebase.firestore.FieldValue.arrayUnion(vendorId),
          ...vendorStatsUpdate,
        }, { merge: true });
      } else {
        await custRef.set({
          customerId: phone.trim(),
          name: name.trim(),
          nameLower: name.trim().toLowerCase(),
          phone: phone.trim(),
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          lastVisit: firebase.firestore.FieldValue.serverTimestamp(),
          totalSpent: totalPrice,
          visitCount: 1,
          source: "walkin",
          vendors: [vendorId],
          vendorStats: {
            [vendorId]: { visits: 1, spent: totalPrice, lastVisit: firebase.firestore.FieldValue.serverTimestamp() },
          },
        });
      }

      setSuccessMsg("Walk-in booking saved!");
      clearForm();
      if (onClose) setTimeout(() => onClose(), 1500);
    } catch (e) {
      console.error(e);
      setErrorMsg("Error saving booking. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const clearForm = () => {
    setName(""); setPhone(""); setPhoneErr("");
    setSelectedServices([]); setEditedPrices({});
    setSvcStylists({}); setDefaultStylist(null);
    setCustomerSelected(false); setSuggestions([]); setShowSuggest(false);
    setScheduleDate(today()); setScheduleTime(nowTime());
    setPaymentMode("Cash");
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  // ─── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="wi_page">
      <div className="wi_header">
        <div>
          <h4 className="wi_title">Add Walk-In</h4>
          <p className="wi_subtitle">Record a walk-in appointment</p>
        </div>
        {onClose && (
          <button className="wi_modal_close" onClick={onClose} title="Close">
            <i className="fa fa-times"></i>
          </button>
        )}
      </div>

      {successMsg && <div className="wi_alert wi_alert_success">{successMsg}</div>}
      {errorMsg   && <div className="wi_alert wi_alert_error">{errorMsg}</div>}

      {/* ── Customer Info ────────────────────────────────────────────────────── */}
      <div className="wi_card">
        <div className="wi_section_label"><i className="fa fa-user"></i> Customer Info</div>

        {/* Name */}
        <div className="wi_field" ref={suggestRef}>
          <label>Full Name</label>
          <input
            type="text"
            value={name}
            placeholder="Search or enter name"
            onChange={(e) => {
              setName(e.target.value);
              setCustomerSelected(false);
              searchCustomers(e.target.value);
            }}
          />
          {showSuggest && suggestions.length > 0 && (
            <div className="wi_suggestions">
              {suggestions.map((c) => (
                <div key={c.id} className="wi_suggestion_item" onClick={() => selectCustomer(c)}>
                  <span className="wi_sug_name">{c.name}</span>
                  <span className="wi_sug_phone">{c.phone}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Phone */}
        <div className="wi_field">
          <label>Phone <span className="wi_hint">+91</span></label>
          <input
            type="tel"
            value={phone}
            maxLength={10}
            placeholder="10-digit number"
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 10);
              setPhone(v);
              setPhoneErr("");
              if (!customerSelected) searchCustomers(v);
            }}
          />
          {phoneErr && <span className="wi_field_err">{phoneErr}</span>}
        </div>

        {/* Date & Time */}
        <div className="wi_field_row">
          <div className="wi_field">
            <label>Date</label>
            <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
          </div>
          <div className="wi_field">
            <label>Time</label>
            <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Services ──────────────────────────────────────────────────────────── */}
      <div className="wi_card">
        <div className="wi_section_label"><i className="fa fa-scissors"></i> Select Services</div>

        {/* Search */}
        <div className="wi_service_search">
          <i className="fa fa-search"></i>
          <input
            type="text"
            placeholder="Search all services..."
            value={serviceSearch}
            onChange={(e) => setServiceSearch(e.target.value)}
          />
          {serviceSearch && (
            <button className="wi_search_clear" onClick={() => setServiceSearch("")}>
              <i className="fa fa-times"></i>
            </button>
          )}
        </div>

        {serviceSearch ? (
          <div className="wi_search_count">
            <i className="fa fa-globe"></i> {filteredSvcs.length} result{filteredSvcs.length !== 1 ? "s" : ""} across all categories
          </div>
        ) : (
          <>
            {/* Category chips */}
            <div className="wi_chips_row">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`wi_chip ${selectedCat === cat ? "wi_chip_active" : ""}`}
                  onClick={() => { setSelectedCat(cat); setSelectedGroup(null); }}
                >
                  {cat}
                </button>
              ))}
            </div>
            {/* Group chips */}
            {groupsInCat.length > 0 && (
              <div className="wi_chips_row wi_chips_sm">
                <button
                  className={`wi_chip wi_chip_sm ${!selectedGroup ? "wi_chip_active_sm" : ""}`}
                  onClick={() => setSelectedGroup(null)}
                >All</button>
                {groupsInCat.map((g) => (
                  <button
                    key={g}
                    className={`wi_chip wi_chip_sm ${selectedGroup === g ? "wi_chip_active_sm" : ""}`}
                    onClick={() => setSelectedGroup(selectedGroup === g ? null : g)}
                  >{g}</button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Service tiles */}
        <div className="wi_service_list">
          {filteredSvcs.length === 0 ? (
            <div className="wi_empty_state">No services found</div>
          ) : filteredSvcs.map((svc) => {
            const id = svc.serviceId || svc.id;
            const selected = isSvcSelected(id);
            const hasDiscount = svc.actualPrice && svc.actualPrice !== svc.offerPrice && svc.offerPrice !== "0";
            return (
              <div
                key={id}
                className={`wi_svc_tile ${selected ? "wi_svc_tile_sel" : ""}`}
                onClick={() => toggleService(svc)}
              >
                <div className={`wi_svc_check ${selected ? "wi_svc_check_sel" : ""}`}>
                  {selected && <i className="fa fa-check"></i>}
                </div>
                <div className="wi_svc_info">
                  <div className="wi_svc_name">{svc.serviceName}</div>
                  <div className="wi_svc_meta">
                    <span><i className="fa fa-clock-o"></i> {svc.serviceTime} min</span>
                    {svc.gender && <span className="wi_svc_gender">{svc.gender}</span>}
                    {svc.group && <span className="wi_svc_group">{svc.group}</span>}
                  </div>
                </div>
                <div className="wi_svc_price">
                  <span className="wi_price_offer">₹{svc.offerPrice || svc.price}</span>
                  {hasDiscount && <span className="wi_price_actual">₹{svc.actualPrice}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Selected Services — editable prices + per-service stylist ─────────── */}
      {selectedServices.length > 0 && (
        <div className="wi_card wi_card_selected">
          <div className="wi_section_label"><i className="fa fa-list-check"></i> Selected Services</div>
          {selectedServices.map((s) => {
            const id = s.serviceId;
            const assignedStylist = svcStylists[id];
            return (
              <div key={id} className="wi_selected_row">
                <div className="wi_sel_header">
                  <span className="wi_sel_name">{s.serviceName}</span>
                  <button
                    className="wi_sel_remove"
                    onClick={() => toggleService(s)}
                    title="Remove"
                  ><i className="fa fa-times"></i></button>
                </div>
                <div className="wi_sel_body">
                  {/* Price edit */}
                  <div className="wi_sel_price_wrap">
                    <label>Price (₹)</label>
                    <input
                      type="number"
                      min="0"
                      className="wi_sel_price_input"
                      value={editedPrices[id] ?? ""}
                      onChange={(e) => { const val = e.target.value; setEditedPrices((prev) => ({ ...prev, [id]: val })); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {/* Stylist chips */}
                  <div className="wi_sel_stylist_wrap">
                    <label>Stylist {!assignedStylist && <span className="wi_required">*required</span>}</label>
                    <div className="wi_stylist_chips">
                      {stylists.map((st) => (
                        <button
                          key={st.id}
                          className={`wi_stylist_chip ${assignedStylist?.id === st.id ? "wi_stylist_chip_sel" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSvcStylists((prev) => ({ ...prev, [id]: { id: st.id, name: st.name } }));
                          }}
                        >
                          {st.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Total ─────────────────────────────────────────────────────────────── */}
      <div className="wi_total_bar">
        <div>
          <div className="wi_total_val">₹{totalPrice.toLocaleString("en-IN")}</div>
          <div className="wi_total_lbl">Total ({selectedServices.length} service{selectedServices.length !== 1 ? "s" : ""} · {totalTime} min)</div>
        </div>
      </div>

      {/* ── Default Stylist quick-assign ──────────────────────────────────────── */}
      {stylists.length > 0 && (
        <div className="wi_card">
          <div className="wi_section_label"><i className="fa fa-user-circle"></i> Assign All to Stylist</div>
          <p className="wi_hint_text">Tap to assign a stylist to all selected services at once</p>
          <div className="wi_stylist_list">
            {stylists.map((st) => (
              <div
                key={st.id}
                className={`wi_stylist_card ${defaultStylist?.id === st.id ? "wi_stylist_card_sel" : ""}`}
                onClick={() => applyDefaultStylist({ id: st.id, name: st.name })}
              >
                <div className="wi_stylist_avatar">
                  {st.image
                    ? <img src={st.image} alt={st.name} />
                    : <span>{(st.name || "?")[0].toUpperCase()}</span>}
                </div>
                <div className="wi_stylist_name">{st.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Payment Mode ─────────────────────────────────────────────────────── */}
      <div className="wi_card">
        <div className="wi_section_label"><i className="fa fa-credit-card"></i> Payment Mode</div>
        <div className="wi_pay_row">
          {["Cash", "UPI", "Card"].map((m) => (
            <button
              key={m}
              className={`wi_pay_btn ${paymentMode === m ? "wi_pay_btn_sel" : ""}`}
              onClick={() => setPaymentMode(m)}
            >
              <i className={`fa ${m === "Cash" ? "fa-money" : m === "UPI" ? "fa-mobile" : "fa-credit-card"}`}></i>
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* ── Save ─────────────────────────────────────────────────────────────── */}
      <button
        className="wi_save_btn"
        disabled={saving}
        onClick={handleSave}
      >
        {saving
          ? <><i className="fa fa-spinner fa-pulse"></i> Saving...</>
          : <><i className="fa fa-check-circle"></i> Save Walk-In</>
        }
      </button>
    </div>
  );
};

export default WalkInForm;
